import Editor from '@monaco-editor/react'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import { Box, IconButton, Portal, Tooltip } from '@mui/material'
import { MRT_RowData } from 'material-react-table'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getInitCellValue, MrtCellProps } from '@/components'
import { getRowElementLabel } from '@/components/EditorModalHeader'
import { useAppContext } from '@/hooks'
import { APP_FONT_FAMILY } from '@/styles/theme'
import { registerSqlAutocomplete } from '@/utils/monaco/sqlAutocomplete'

import { useSelectRowOnOpen } from '../hooks/useSelectRowOnOpen'
import { focusAdjacentTableCell } from '../utils'
import { CellTooltip } from './CellTooltip'
import { EditorLanguage, FullscreenEditorModal } from './FullscreenEditorModal'
import { MultilinePreview } from './MultilinePreview'

interface MonacoScriptCellProps<TData extends MRT_RowData>
	extends MrtCellProps<TData> {
	language?: EditorLanguage
}

const EXPANDED_EDITOR_HEIGHT = 150
const MIN_TOOLTIP_LENGTH = 13
const DISPLAY_VALUE_TRUNCATE_LENGTH = 50
const MIN_CELL_HEIGHT = 32
const FULLSCREEN_TOGGLE_DELAY_MS = 50

const useElementPosition = (ref: React.RefObject<HTMLElement>, isExpanded: boolean) => {
	const [position, setPosition] = useState<DOMRect | null>(null)

	useEffect(() => {
		if (!ref.current) {
			setPosition(null)
			return
		}

		const findTableCell = (el: HTMLElement | null): HTMLElement | null => {
			while (el) {
				if (el.tagName === 'TD' || el.tagName === 'TH') {
					return el
				}
				el = el.parentElement
			}
			return null
		}

		const tableCell = findTableCell(ref.current)
		const targetElement = tableCell || ref.current

		const updatePosition = () => {
			setPosition(targetElement.getBoundingClientRect())
		}

		updatePosition()
		
		if (isExpanded) {
			window.addEventListener('scroll', updatePosition, true)
			window.addEventListener('resize', updatePosition)

			return () => {
				window.removeEventListener('scroll', updatePosition, true)
				window.removeEventListener('resize', updatePosition)
			}
		}
	}, [ref, isExpanded])

	return position
}

/**
 * Script Cell for MRT tables with expandable inline editor
 *
 * Click on cell to expand Monaco editor inline (rolls down within cell).
 * Fullscreen modal available via icon.
 *
 * Keyboard shortcuts:
 * - F11: Open fullscreen editor
 * - Ctrl+S / Cmd+S: Save and collapse
 * - Esc: Cancel and collapse
 */
export const MonacoScriptCell = <TData extends MRT_RowData>({
	cell,
	column,
	isDisabled = false,
	isReadOnly = false,
	row,
	updateCellValue,
	customValue,
	language = 'sql',
}: MonacoScriptCellProps<TData>) => {
	const { t } = useAppContext()
	const cellValue = customValue !== undefined ? customValue : cell?.getValue()
	const [editorValue, setEditorValue] = useState<string>(
		getInitCellValue(customValue, cellValue),
	)
	const [isFullscreenOpen, setIsFullscreenOpen] = useState(false)
	const [isExpanded, setIsExpanded] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)
	const editorRef = useRef<any>(null)

	const cellPosition = useElementPosition(containerRef, isExpanded)

	useEffect(() => {
		setEditorValue(getInitCellValue(customValue, cellValue))
	}, [cellValue, customValue])

	// Focus editor when expanded and place cursor at end
	useEffect(() => {
		if (isExpanded) {
			const timer = setTimeout(() => {
				const editor = editorRef.current
				if (editor) {
					editor.focus()
					// Place cursor at end of text and scroll to it
					const model = editor.getModel()
					if (model) {
						const lastLine = model.getLineCount()
						const lastColumn = model.getLineMaxColumn(lastLine)
						editor.setPosition({ lineNumber: lastLine, column: lastColumn })
						editor.revealLineInCenter(lastLine)
					}
				}
			}, 50)
			return () => clearTimeout(timer)
		}
	}, [isExpanded])

	const handleSave = useCallback(
		(focusAfter?: 'current' | 'next' | 'prev') => {
			if (!isDisabled && !isReadOnly && editorValue !== cellValue) {
				updateCellValue?.(
					row?.index as number,
					column?.id as string,
					editorValue as TData[string],
				)
			}
			setIsExpanded(false)
			setTimeout(() => {
				if (focusAfter === 'next' || focusAfter === 'prev') {
					focusAdjacentTableCell(containerRef, focusAfter)
				} else {
					const focusableBox = containerRef.current?.querySelector('[tabindex="0"]') as HTMLElement
					focusableBox?.focus()
				}
			}, 50)
		},
		[
			editorValue,
			cellValue,
			isDisabled,
			isReadOnly,
			row?.index,
			column?.id,
			updateCellValue,
		],
	)

	const handleCancel = useCallback(() => {
		setEditorValue(getInitCellValue(customValue, cellValue))
		setIsExpanded(false)
		// Focus back on the cell for continued keyboard navigation
		setTimeout(() => {
			const focusableBox = containerRef.current?.querySelector('[tabindex="0"]') as HTMLElement
			focusableBox?.focus()
		}, 50)
	}, [cellValue, customValue])

	const handleFullscreenSave = useCallback(
		(value: string) => {
			setEditorValue(value)
			updateCellValue?.(
				row?.index as number,
				column?.id as string,
				value as TData[string],
			)
			setIsFullscreenOpen(false)
			// Focus back on the cell for continued keyboard navigation
			setTimeout(() => {
				const focusableBox = containerRef.current?.querySelector('[tabindex="0"]') as HTMLElement
				focusableBox?.focus()
			}, 50)
		},
		[row?.index, column?.id, updateCellValue],
	)

	const handleFullscreenClose = useCallback(() => {
		setIsFullscreenOpen(false)
		// Focus back on the cell for continued keyboard navigation
		setTimeout(() => {
			const focusableBox = containerRef.current?.querySelector('[tabindex="0"]') as HTMLElement
			focusableBox?.focus()
		}, 50)
	}, [])

	useEffect(() => {
		if (!isExpanded) return

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Tab') {
				e.preventDefault()
				e.stopPropagation()
				handleSave(e.shiftKey ? 'prev' : 'next')
				return
			}
			if ((e.ctrlKey || e.metaKey) && e.key === 's') {
				e.preventDefault()
				handleSave()
			}
			if (e.key === 'Escape') {
				e.preventDefault()
				handleSave()
			}
			if (e.key === 'F11') {
				e.preventDefault()
				if (isFullscreenOpen) {
					setIsFullscreenOpen(false)
				} else {
					setIsFullscreenOpen(true)
					setTimeout(() => setIsExpanded(false), FULLSCREEN_TOGGLE_DELAY_MS)
				}
			}
		}

		// Capture phase so Tab is handled before Monaco (avoids Tab inserting indent + our save/move)
		window.addEventListener('keydown', handleKeyDown, true)
		return () => window.removeEventListener('keydown', handleKeyDown, true)
	}, [isExpanded, isFullscreenOpen, handleSave, handleCancel])

	useSelectRowOnOpen(isExpanded || isFullscreenOpen, row?.original)

	const effectiveReadOnly = isDisabled || isReadOnly
	const columnName = column?.columnDef.header ?? ''
	const elementLabel = getRowElementLabel(row?.original)
	const hasContent = editorValue && editorValue.trim().length > 0
	const showTooltip = (editorValue?.length || 0) > MIN_TOOLTIP_LENGTH

	const codePreview = useMemo(
		() =>
			editorValue ? <MultilinePreview value={editorValue} showLineNumbers /> : null,
		[editorValue],
	)

	const displayValue = editorValue
		? editorValue.length > DISPLAY_VALUE_TRUNCATE_LENGTH
			? editorValue.substring(0, DISPLAY_VALUE_TRUNCATE_LENGTH) + '...'
			: editorValue
		: ''

	return (
		<>
			<Box
				ref={containerRef}
				sx={{
					position: 'relative',
					width: '100%',
					minHeight: MIN_CELL_HEIGHT,
				}}
			>
				{!isExpanded && (
					<CellTooltip
						title={showTooltip ? editorValue : undefined}
						isMenuOpen={isFullscreenOpen}
						customContent={showTooltip ? codePreview : undefined}
						isEditMode={!effectiveReadOnly}
					>
						<Box
							tabIndex={effectiveReadOnly ? -1 : 0}
							sx={{
								display: 'flex',
								alignItems: 'center',
								width: '100%',
								minHeight: MIN_CELL_HEIGHT,
								gap: 0.5,
								cursor: effectiveReadOnly ? 'default' : 'text',
								'&:focus': {
									outline: 'none',
								},
							}}
							onClick={() => !effectiveReadOnly && setIsExpanded(true)}
							onKeyDown={(e) => {
								if (!effectiveReadOnly && e.key === 'Enter') {
									e.preventDefault()
									e.stopPropagation()
									setIsExpanded(true)
								}
							}}
						>
							<Box
								component="span"
								sx={{
									flex: 1,
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
									color: hasContent ? 'inherit' : 'text.disabled',
								}}
							>
								{displayValue || '—'}
							</Box>

							<Box
								sx={{
									display: 'flex',
									opacity: 0,
									transition: 'opacity 0.15s',
									'.MuiTableCell-root:hover &, .MuiTableCell-root:focus-within &':
										{
											opacity: 1,
										},
								}}
							>
								<Tooltip title={t('TOGGLE_FULLSCREEN')}>
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation()
											setIsFullscreenOpen(true)
										}}
										sx={{ p: 0.25 }}
									>
										<FullscreenIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</Tooltip>
							</Box>
						</Box>
					</CellTooltip>
				)}

			</Box>

			{isExpanded && cellPosition && (
				<Portal>
					<Box
						onClick={handleSave}
						sx={{
							position: 'fixed',
							inset: 0,
							zIndex: 1200,
							bgcolor: 'transparent',
						}}
					/>
					<Box
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => {
							// Stop propagation for arrow keys to prevent cell navigation
							if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
								e.stopPropagation()
							}
							// Ctrl/Cmd + S: Save and close
							if ((e.ctrlKey || e.metaKey) && e.key === 's') {
								e.preventDefault()
								e.stopPropagation()
								handleSave()
							}
							// Escape: Save and close
							if (e.key === 'Escape') {
								e.preventDefault()
								e.stopPropagation()
								handleSave()
							}
						}}
						sx={{
							position: 'fixed',
							top: cellPosition.top,
							right: window.innerWidth - cellPosition.right,
							width: cellPosition.width,
							zIndex: 1201,
							overflow: 'hidden',
						}}
					>
						<Box
							sx={{
								borderLeft: '1px solid',
								borderRight: '1px solid',
								borderBottom: '1px solid',
								borderColor: 'primary.main',
								borderBottomLeftRadius: 4,
								borderBottomRightRadius: 4,
								overflow: 'hidden',
								bgcolor: 'background.paper',
								animation: 'slideDown 0.2s ease-out',
								'@keyframes slideDown': {
									from: {
										transform: 'translateY(-100%)',
										opacity: 0,
									},
									to: {
										transform: 'translateY(0)',
										opacity: 1,
									},
								},
							}}
						>
						<Editor
							height={EXPANDED_EDITOR_HEIGHT}
							language={language}
							value={editorValue}
							onChange={(value) => setEditorValue(value ?? '')}
							options={{
								readOnly: effectiveReadOnly,
								minimap: { enabled: false },
								fontSize: 13,
								fontFamily: APP_FONT_FAMILY,
								lineNumbers: 'off',
								wordWrap: 'on',
								automaticLayout: true,
								scrollBeyondLastLine: false,
								tabSize: 2,
								theme: 'vs-light',
								folding: false,
								glyphMargin: false,
								lineDecorationsWidth: 4,
								lineNumbersMinChars: 0,
								renderLineHighlight: 'none',
								scrollbar: {
									vertical: 'auto',
									horizontal: 'hidden',
									verticalScrollbarSize: 8,
								},
								overviewRulerLanes: 0,
								hideCursorInOverviewRuler: true,
								overviewRulerBorder: false,
								padding: { top: 8, bottom: 8 },
								suggestFontSize: 12,
								suggestLineHeight: 18,
								suggest: {
									showStatusBar: false,
									showIcons: true,
									maxVisibleSuggestions: 6,
								},
								fixedOverflowWidgets: true,
							}}
							onMount={(editor, monaco) => {
								editorRef.current = editor
								registerSqlAutocomplete(monaco)
								
								// Add Ctrl/Cmd+S shortcut to save and close
								editor.addCommand(
									monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
									() => {
										handleSave()
									}
								)
								
								// Add Escape shortcut to save and close
								editor.addCommand(
									monaco.KeyCode.Escape,
									() => {
										handleSave()
									}
								)
								
								editor.focus()
							}}
						/>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'flex-end',
								alignItems: 'center',
								px: 1,
								py: 0.25,
								bgcolor: 'grey.50',
								borderTop: '1px solid',
								borderColor: 'divider',
							}}
						>
							<Tooltip title={t('TOGGLE_FULLSCREEN')}>
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation()
										setIsFullscreenOpen(true)
										setTimeout(() => setIsExpanded(false), FULLSCREEN_TOGGLE_DELAY_MS)
									}}
									sx={{ p: 0.25 }}
								>
									<FullscreenIcon sx={{ fontSize: 14 }} />
								</IconButton>
							</Tooltip>
						</Box>
						</Box>
					</Box>
				</Portal>
			)}

			<FullscreenEditorModal
				open={isFullscreenOpen}
				onClose={handleFullscreenClose}
				onSave={handleFullscreenSave}
				value={editorValue}
				title={String(columnName)}
				elementLabel={elementLabel}
				language={language}
				readOnly={effectiveReadOnly}
			/>
		</>
	)
}
